import LazyImage from "components/elements/LazyImage";
import classes from "./classes.module.scss";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import Typography from "components/elements/Typography";
import CreativeSourceResponseResource from "common/resources/Project/Config/CreativeSource/CreativeSourceResponseResource";
import { translateISO369 } from "utils/I18nText";
import { container } from "tsyringe";
import CreativeSourceService from "services/projects/CreativeSourceService";
import { useCallback, useEffect, useState } from "react";
import { useProjectId } from "hooks/useProjectId";

const creativeSourceService = container.resolve(CreativeSourceService);

type IProps = {
	creativeSource: CreativeSourceResponseResource;
	onEdit: (creativeSource: CreativeSourceResponseResource) => void;
	onDelete: (creativeSource: CreativeSourceResponseResource) => void;
};

export default function CreativeSource(props: IProps) {
	const { imageUrl } = useCreativeSourceImage(props.creativeSource);

	const onEditClick = useCallback(() => {
		props.onEdit(props.creativeSource);
	}, [props]);

	const onDeleteClick = useCallback(() => {
		props.onDelete(props.creativeSource);
	}, [props]);

	return (
		<div className={classes["root"]}>
			<div className={classes["content"]}>
				<LazyImage src={imageUrl} alt="Creative Source" className={classes["image"]} />
				<Typography type="p" size="small" weight="medium" typoFont="secondary">
					{translateISO369(props.creativeSource.description)}
				</Typography>
			</div>
			<div className={classes["button-container"]}>
				<PencilIcon className={classes["icon"]} onClick={onEditClick} />
				<TrashIcon className={classes["icon"]} onClick={onDeleteClick} />
			</div>
		</div>
	);
}

export const useCreativeSourceImage = (creativeSource: CreativeSourceResponseResource) => {
	const { projectId } = useProjectId();
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [imageFile, setImageFile] = useState<File | null>(null);

	useEffect(() => {
		const { image } = creativeSource;
		if (!image) return;
		creativeSourceService.downloadImage(projectId, creativeSource.id).then((blob) => {
			setImageUrl(URL.createObjectURL(blob));
			setImageFile(new File([blob], image.name, { type: blob?.type || image.mimeType }));
		});
	}, [projectId, creativeSource]);

	return { imageUrl, imageFile };
};
